import StaticPage from '../_StaticPage'

// The public credits page the Terms of Service points to. It summarizes, in
// prose, the same providers the product already credits as DATA: the marine
// tileset manifest's per-tileset `attribution` (shown behind the map's
// attribution control), the `attribution[]` on every forecast payload, and the
// station `attribution` field in the API. Those remain the authoritative
// per-layer and per-station credits; this page is the human-readable roll-up.
//
// KEEP IN SYNC. When a data source is added anywhere in the stack (a new
// tileset in the marine manifest, a new forecast model, warning issuer or
// radar network in ../marine-weather, a new national tide provider here), add
// it to this page in the same change. Checklist and the verified licence
// wording: docs/context/project_legal_pages_refresh_2026_09.md and
// docs/context/reference_chart_data_licensing.md.
export default function DataSources() {
  return (
    <StaticPage
      title="Data Sources"
      description="The government agencies, hydrographic offices, weather services and open-data projects whose data Tides Near Me is built on, with their licences."
    >
      <p>
        <b>Last updated:</b> October 2026
      </p>
      <p>
        Tides Near Me is built on data published by national agencies, hydrographic offices,
        meteorological services, and open-data communities. This page lists them and the licences
        their data is used under. The exact credits for what is on screen appear inside the Service:
        in the map's attribution control for each map layer, alongside each marine forecast, and in
        our API responses. None of these providers is affiliated with or endorses Tides Near Me, and
        none has reviewed how its data is presented here.
      </p>
      <p>
        <b>Not for navigation.</b> Depths and chart features are derived from survey compilations
        and may be inaccurate or out of date. See our <a href="/terms">Terms of Service</a>.
      </p>
      <hr />
      <h2>Base Map</h2>
      <ul>
        <li>
          Map tiles by <b>OpenFreeMap</b>, map style © <b>OpenMapTiles</b>, map data ©{' '}
          <b>OpenStreetMap contributors</b>, available under the Open Database License (ODbL).
          OpenStreetMap data also supplies navigation marks, points of interest, and land areas in
          the marine layers.
        </li>
      </ul>
      <hr />
      <h2>Depths and Nautical Chart Features</h2>
      <p>
        Depth colors, contours, soundings, and chart features are compiled from the following
        sources. Vertical datums differ by source and are shown in the map legend where known.
      </p>
      <h3>Global</h3>
      <ul>
        <li>Bathymetry: GEBCO Compilation Group (2026) GEBCO 2026 Grid</li>
        <li>
          Bathymetry: EMODnet Bathymetry Consortium (2024) EMODnet Digital Bathymetry (DTM 2024), CC
          BY 4.0
        </li>
      </ul>
      <h3>United States</h3>
      <ul>
        <li>Chart features: NOAA Electronic Navigational Charts (public domain)</li>
        <li>Bathymetry: NOAA Office of Coast Survey BlueTopo</li>
        <li>Bathymetry: NOAA NCEI Great Lakes Bathymetry</li>
        <li>Marine protected areas: NOAA MPA Inventory</li>
        <li>Inland Electronic Navigational Charts: US Army Corps of Engineers</li>
      </ul>
      <h3>Canada</h3>
      <ul>
        <li>Bathymetry: Canadian Hydrographic Service NONNA data</li>
      </ul>
      <h3>United Kingdom and Ireland</h3>
      <ul>
        <li>
          Contains United Kingdom Hydrographic Office data © Crown copyright and database right,
          used under the Open Government Licence v3.0
        </li>
        <li>
          Bathymetry: Contains Irish Public Sector Data (Geological Survey Ireland & Marine
          Institute) licensed under a Creative Commons Attribution 4.0 International (CC BY 4.0)
          licence
        </li>
      </ul>
      <h3>France</h3>
      <ul>
        <li>
          Bathymetry: Shom (HOMONIM MNTs), Licence Ouverte v2.0. Bathymétrie: Litto3D® Shom/IGN
        </li>
        <li>
          Wrecks, obstructions, cables and pipelines: © Shom - Epaves et obstructions répertoriées
          par le Shom; Conduites et câbles sous-marins répertoriés par le Shom. Licence CC BY-SA
          4.0. These features have been re-projected, tiled, and re-styled for display; they are not
          presented in Shom's original form. Ne pas utiliser pour la navigation.
        </li>
      </ul>
      <h3>Australia and New Zealand</h3>
      <ul>
        <li>Bathymetry: © Commonwealth of Australia (Geoscience Australia) 2026, CC BY 4.0</li>
        <li>
          Chart data: Contains data sourced from the LINZ Data Service licensed for reuse under CC
          BY 4.0
        </li>
      </ul>
      <hr />
      <h2>Tide and Tidal Current Predictions</h2>
      <p>
        Where a national agency publishes official predictions, we use them. Elsewhere, predictions
        are computed by harmonic analysis from published tidal constituents. Providers include:
      </p>
      <ul>
        <li>NOAA Center for Operational Oceanographic Products and Services (United States)</li>
        <li>Canadian Hydrographic Service, Fisheries and Oceans Canada (Canada)</li>
        <li>
          UK Hydrographic Office (United Kingdom and Ireland): © Crown Copyright and/or database
          rights. Reproduced by permission of the Controller of His Majesty's Stationery Office and
          the UK Hydrographic Office (www.ukho.gov.uk)
        </li>
        <li>Shom, Service hydrographique et océanographique de la Marine (France)</li>
        <li>
          Australia: Reproduced by permission of the Bureau of Meteorology, © 2026 Commonwealth of
          Australia. <a href="https://www.bom.gov.au/copyright">www.bom.gov.au/copyright</a>
        </li>
        <li>Land Information New Zealand (New Zealand)</li>
        <li>Danish Meteorological Institute (Denmark)</li>
        <li>Rijkswaterstaat (Netherlands)</li>
        <li>Instituto Hidrográfico (Portugal)</li>
        <li>Puertos del Estado (Spain)</li>
        <li>Bundesamt für Seeschifffahrt und Hydrographie (Germany)</li>
        <li>Korea Hydrographic and Oceanographic Agency (Korea)</li>
        <li>
          Japan Meteorological Agency (JMA), 気象庁 — 潮位表 (tide level tables){' '}
          <a href="https://www.data.jma.go.jp/kaiyou/db/tide/suisan/">
            www.data.jma.go.jp/kaiyou/db/tide/suisan
          </a>
          , Public Data License 1.0
        </li>
        <li>
          Japan Coast Guard (JCG), 海上保安庁 — 潮汐推算 (tide predictions){' '}
          <a href="https://www1.kaiho.mlit.go.jp/TIDE/pred2/TidePred/TidePred.htm">
            www1.kaiho.mlit.go.jp/TIDE/pred2
          </a>
          , Government of Japan Standard Terms of Use 2.0; high and low waters derived from the
          hourly predictions by Tides Near Me
        </li>
        <li>Central Weather Administration (Taiwan)</li>
        <li>Hong Kong Observatory (Hong Kong)</li>
        <li>
          Secretaría de Marina (SEMAR), México — Tablas Numéricas de Predicción de Mareas{' '}
          <a href="https://oceanografia.semar.gob.mx/mapa_estaciones.html">
            oceanografia.semar.gob.mx
          </a>
        </li>
        <li>
          Survey of India, Government of India — Tidal prediction data (monthly){' '}
          <a href="https://surveyofindia.gov.in/pages/tidal">surveyofindia.gov.in/pages/tidal</a>.
          Source: Survey of India, Government of India.
        </li>
        <li>
          TICON-4 tidal constants (Hart-Davis, Dettmering and Seitz, 2025), distributed by the open
          tide database <a href="https://doi.org/10.17882/109129">doi.org/10.17882/109129</a>, CC BY
          4.0 (Mexico, India) — predictions computed by Tides Near Me
        </li>
        <li>
          EOT20 ocean tide model tidal constants (Hart-Davis et al., 2021, DGFI-TUM){' '}
          <a href="https://doi.org/10.17882/79489">doi.org/10.17882/79489</a>, CC BY 4.0 (India,
          where the Survey of India has not published a month) — predictions computed by Tides Near
          Me
        </li>
        <li>
          Hourly sea-level observations from the University of Hawaii Sea Level Center
          (Visakhapatnam, India) — tidal constants and predictions computed by Tides Near Me
        </li>
        <li>
          Tide-gauge observations from the Secretaría de Marina (SEMAR), México (San Felipe, Isla
          Mujeres, Puerto Peñasco) — tidal constants and predictions computed by Tides Near Me
        </li>
      </ul>
      <hr />
      <h2>Marine Weather Forecasts</h2>
      <p>Wind, sea state, swell, and other forecast values come from these models and services:</p>
      <ul>
        <li>
          NOAA Global Forecast System (GFS), GFS-Wave, High-Resolution Rapid Refresh (HRRR), and
          National Blend of Models (public domain)
        </li>
        <li>Data source: NOAA/National Weather Service (forecast text, United States)</li>
        <li>Data source: Deutscher Wetterdienst (EWAM wave model and forecasts, Europe)</li>
        <li>Source: Météo-France - modèle de vagues MFWAM (Licence Ouverte 2.0)</li>
        <li>Contains Met Office data © Crown copyright, licensed CC BY-SA 4.0 (wave model, UK)</li>
        <li>
          Australia: Reproduced by permission of the Bureau of Meteorology, © 2026 Commonwealth of
          Australia. <a href="https://www.bom.gov.au/copyright">www.bom.gov.au/copyright</a>
        </li>
      </ul>
      <hr />
      <h2>Marine Warnings</h2>
      <p>
        Official marine warnings are reproduced without alteration of their content or intent, as
        issued by:
      </p>
      <ul>
        <li>NOAA/National Weather Service (United States)</li>
        <li>
          Data Source: Environment and Climate Change Canada. Contains information licenced under
          the Data Server End-use Licence of Environment and Climate Change Canada
        </li>
        <li>Deutscher Wetterdienst (Germany)</li>
        <li>Fuente: AEMET (Spain)</li>
        <li>IPMA, Instituto Português do Mar e da Atmosfera (Portugal)</li>
        <li>Met Éireann (Ireland)</li>
        <li>MET Norway (Norway)</li>
        <li>SMHI (Sweden)</li>
        <li>MeteoAlarm (EUMETNET), CC BY 4.0 (Europe)</li>
        <li>
          Australia: Reproduced by permission of the Bureau of Meteorology, © 2026 Commonwealth of
          Australia. <a href="https://www.bom.gov.au/copyright">www.bom.gov.au/copyright</a>
        </li>
        <li>Japan Meteorological Agency (Japan)</li>
        <li>Korea Meteorological Administration (Korea)</li>
        <li>Central Weather Administration (Taiwan), Open Government Data License v1.0</li>
      </ul>
      <hr />
      <h2>Weather Radar</h2>
      <p>
        Radar imagery is composited from national and regional networks, depending on the region:
      </p>
      <ul>
        <li>Source: NOAA/NSSL Multi-Radar/Multi-Sensor (MRMS) (United States)</li>
        <li>Data Source: Environment and Climate Change Canada</li>
        <li>Source: EUMETNET OPERA weather radar network (CC BY 4.0) (Europe)</li>
        <li>
          Source: Deutscher Wetterdienst (DWD), licensed under GeoNutzV / DL-DE BY 2.0 (Germany)
        </li>
        <li>Radar data © KNMI (Royal Netherlands Meteorological Institute), licensed CC BY 4.0</li>
        <li>Radar data © DMI (Danish Meteorological Institute), licensed CC BY 4.0</li>
        <li>Radar data © Crown copyright, Met Office, licensed CC BY-SA 4.0 (United Kingdom)</li>
        <li>
          Dati radar © Presidenza del Consiglio dei Ministri - Dipartimento della Protezione Civile,
          licensed CC BY-SA (Italy)
        </li>
        <li>Source: Japan Meteorological Agency (JMA)</li>
        <li>
          Data source: Central Weather Administration (CWA), Taiwan - Open Government Data License
          v1.0
        </li>
        <li>
          Australia: Reproduced by permission of the Bureau of Meteorology, © 2026 Commonwealth of
          Australia. <a href="https://www.bom.gov.au/copyright">www.bom.gov.au/copyright</a>
        </li>
      </ul>
      <hr />
      <h2>Licences</h2>
      <p>
        Data marked CC BY 4.0 or CC BY-SA 4.0 is used under the Creative Commons Attribution or
        Attribution-ShareAlike 4.0 International licences. Data marked ODbL is used under the Open
        Database License 1.0. UK public sector data is used under the Open Government Licence v3.0,
        and French public sector data under the Licence Ouverte 2.0 (Etalab). United States federal
        data is in the public domain. Australian Bureau of Meteorology data is reproduced under a
        Bureau of Meteorology Data Licence Agreement; the Bureau does not endorse this Service, and
        anyone wanting the Bureau's data in its original form should obtain it from the Bureau
        directly. Environment and Climate Change Canada data is used under its Data Server End-use
        Licence and carries no official status here. Where we have adapted data, for example by
        re-projecting, tiling, banding, or re-styling it, the result is ours and the adaptation is
        indicated here; the underlying data remains under its original licence. Where a licence
        requires derived material to be shared under the same licence, the layers derived from that
        source are available on request under that licence.
      </p>
      <p>
        Questions about attribution or licensing:{' '}
        <a href="mailto:support@tidesnear.me">support@tidesnear.me</a>
      </p>
    </StaticPage>
  )
}
